import React, { useCallback } from 'react';
import { getIn, useFormikContext } from 'formik';
import { Select } from '../../Select/Select';
import { SelectProps, OptionTypeWithCode } from '../../Select/types';
import { useAppFormControlContext } from './appFormControlContext';
import { appFormDataTestFactory } from './appFormDataTestFactory';

type AppFormSelectProps<MULTI extends boolean = false, VALUE extends unknown = string> = SelectProps<MULTI, VALUE> & {
  name?: string;
};

export const AppFormSelect = <MULTI extends boolean = false, VALUE extends unknown = string>(
  props: AppFormSelectProps<MULTI, VALUE>
) => {
  const context = useAppFormControlContext();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const name = 'name' in props ? props.name : context.name;
  const value = 'value' in props ? props.value : name ? getIn(values, name) : undefined;
  const multiple = 'multiple' in props;
  const error = 'error' in props ? props.error : !!context.error;
  const onChange = props.onChange;
  const onOptionChange = props.onOptionChange;
  const handleOnOptionChange = useCallback(
    (option?: OptionTypeWithCode<VALUE> | OptionTypeWithCode<VALUE>[]) => {
      const valueToChange = Array.isArray(option) ? (option?.map((it) => it.value) ?? []) : option?.value;
      if (onChange || onOptionChange) {
        onChange?.(valueToChange as any);
        onOptionChange?.(option as any);
      } else if (name) {
        setFieldTouched(name);
        setFieldValue(name, valueToChange);
      }
    },
    [setFieldTouched, setFieldValue, name, onChange, onOptionChange]
  );
  return (
    <Select
      {...(props as unknown as SelectProps)}
      error={error}
      value={value ?? (multiple ? EMPTY : undefined)}
      required={false}
      onChange={undefined}
      onOptionChange={handleOnOptionChange}
      id={(props.id ?? context.name) ? `${context.name}-input` : undefined}
      dataTest={appFormDataTestFactory(props.dataTest ?? context.dataTest).input}
    />
  );
};

const EMPTY: any[] = [];
